import { ArrowLeft, CreditCard } from 'lucide-react';
import StripeElement from 'components/StripeElement';
import { formatMoney, HOUSEHOLD_LABELS, LABEL, OUTLINE_PLAN_BTN } from './helper';
import moment from 'moment';
import { DATE_FORMATS } from 'utils/helpers';

// `unpaid` is the first payment, not a failed renewal: there is no card on
// file to retry and no membership yet to keep running.
const RecoverPayment = ({ enrollment, recovery, onSuccess, onBack, unpaid = false }) => {
  const { plan_name, household_type } = enrollment || {};
  const { amount_due, client_secret, next_attempt_at, hosted_invoice_url } = recovery || {};

  return (
    <div className="animate-slide-up">
      <div className="mb-4 flex items-start gap-4 rounded-xl border border-amber-100 bg-amber-50 p-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-white text-amber-600">
          <CreditCard size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-secondary-900">
            {unpaid ? 'Finish joining' : 'Update your payment method'}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800">
            {unpaid ? (
              <>
                Enter a card below to pay {formatMoney(amount_due)} and start your {plan_name}{' '}
                membership. Nothing has been charged yet.
              </>
            ) : (
              <>
                Enter a card below to settle the outstanding {formatMoney(amount_due)} and keep your{' '}
                {plan_name} membership running.
                {next_attempt_at
                  ? ` Otherwise we'll try the card on file again on ${moment(next_attempt_at).format(DATE_FORMATS.medium)}.`
                  : " This was our last automatic attempt, so the membership ends if it isn't paid."}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-secondary-200 bg-secondary-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className={LABEL}>{unpaid ? 'Due now' : 'Outstanding payment'}</p>
          <p className="mt-1 text-base font-bold text-secondary-900">
            {plan_name} · {HOUSEHOLD_LABELS[household_type] || household_type}
          </p>
        </div>

        <p className="shrink-0 text-2xl font-extrabold text-secondary-900 sm:text-right">
          {formatMoney(amount_due)}
        </p>
      </div>

      {/* The secret belongs to the failed invoice, so confirming it pays that
          invoice — which is what moves the membership back to active. */}
      <StripeElement clientSecret={client_secret} onSuccess={onSuccess} />

      <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={onBack} className={`${OUTLINE_PLAN_BTN} shrink-0 gap-2 px-4`}>
          <ArrowLeft size={15} />
          {unpaid ? 'Back' : 'Back to my membership'}
        </button>

        {hosted_invoice_url && (
          <a
            href={hosted_invoice_url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-semibold text-secondary-400 underline underline-offset-2 hover:text-secondary-600"
          >
            View this invoice on Stripe
          </a>
        )}
      </div>
    </div>
  );
};

export default RecoverPayment;
